import React, { useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';

import BottomSheet from 'components/DesignSystem/BottomSheet';
import Icon from 'components/DesignSystem/Icon';
import useDragScroll from 'utils/hooks/useDragScroll';
import shortenedTabPath from 'helper/shortenedTabPath';
import { cssVar } from 'constants/theme';

import './index.scss';

export interface SectionTabItem {
  title: string;
  path: string;
}

export default function SectionTabs({
  tabs,
  sheetTitle = 'Seções',
}: {
  tabs: SectionTabItem[];
  sheetTitle?: string;
}) {
  const location = useLocation();
  const navigate = useNavigate();
  const {
    ref,
    dragHandlers: {
      onPointerDown, onPointerMove, onPointerUp, onPointerLeave, onClickCapture,
    },
  } = useDragScroll<HTMLDivElement>();
  const [sheetOpen, setSheetOpen] = useState(false);

  const activePath = shortenedTabPath(location.pathname);

  const goTo = (path: string) => {
    setSheetOpen(false);
    navigate(path);
  };

  return (
    <div className="section-tabs">
      <div
        className="section-tabs-scroller"
        ref={ref}
        onPointerDown={onPointerDown}
        onPointerMove={onPointerMove}
        onPointerUp={onPointerUp}
        onPointerLeave={onPointerLeave}
        onClickCapture={onClickCapture}
      >
        {tabs.map((tab) => (
          <Link
            key={tab.path}
            to={tab.path}
            className={`section-tabs-pill${tab.path === activePath ? ' active' : ''}`}
          >
            {tab.title}
          </Link>
        ))}
      </div>
      <button
        type="button"
        className="section-tabs-grid-button"
        aria-label="Ver todas as seções"
        onClick={() => setSheetOpen(true)}
      >
        <Icon iconType="grid_view_outline" size="small" />
      </button>
      <BottomSheet open={sheetOpen} dismiss={() => setSheetOpen(false)} title={sheetTitle}>
        <ul className="section-tabs-sheet-list list-unstyled mb-0">
          {tabs.map((tab) => {
            const isActive = tab.path === activePath;
            return (
              <li key={tab.path}>
                <button
                  type="button"
                  className={`section-tabs-sheet-item${isActive ? ' active' : ''}`}
                  onClick={() => goTo(tab.path)}
                >
                  <span>{tab.title}</span>
                  {isActive && <Icon iconType="checkmark" color={cssVar('text-accent')} />}
                </button>
              </li>
            );
          })}
        </ul>
      </BottomSheet>
    </div>
  );
}
